import { useCallback, useMemo, useRef, memo, useEffect, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  Task,
  TaskFilterInputObject,
  TaskScopeInputObject,
  TaskScopeNameEnum,
} from '@lib/graphql-common';
import { TableMultiselectHandle } from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import ModalTableMultiunselect from '@lib/components/ModalTableMultiselect/ModalTableMultiunselect';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import { ModalTableMultiselectMode } from '@lib/components/ModalTableMultiselect/enums';
import useTasksMultiselectQueryHook from '@lib/utils/fetch/hooks/useTasksMultiselectQueryHook';
import { Values } from '@lib/interfaces/form';
import useFilterFields, {
  FieldNames as FilterFieldNames,
} from './utils/getFilterFields';
import getTasksColumns from './utils/getTasksColumns';
import { createAdditionalFilters } from './utils/createAdditionalFilters';
import { TasksTableMultiselectFilterLoadOptions } from './types';
import useTaskPickerState, {
  useAllTabSnapshot,
} from '../utils/useTaskPickerState';
import TaskPickerTable from '../utils/TaskPickerTable';

export type TasksTableMultiselectProps = {
  /** App-specific search lists backing the Sector/Site/Area/Equipment/Category filters. */
  filterLoadOptions: TasksTableMultiselectFilterLoadOptions;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (values: string[], selectedItems?: Task[]) => void;
  /**
   * Renders the picker body on its own, without the surrounding `Modal` and
   * its Cancel / Confirm buttons, for a host that owns that chrome — e.g. one
   * step of Intervention's `TaskAndCompletionsModal` wizard. The host drives
   * confirmation through `ref` and is told about it via `onSelectionConfirmed`;
   * `onClose` goes unused. Not supported together with
   * `ModalTableMultiselectMode.SelectedOnly`.
   */
  inline?: boolean;
  ref?: Ref<TableMultiselectHandle>;
  /** Inline mode only: fired once a `confirmSelection()` run has emitted `onSelect`. */
  onSelectionConfirmed?: () => void;
  formRef: FormRefType;
  fieldName?: string;
  /**
   * Pre-selected ids to use instead of the ones read off `formRef`'s
   * `fieldName` — for a host that holds the pick outside the form until a
   * later confirmation (e.g. Intervention's `TaskAndCompletionsModal` stages
   * step 1 and only commits it on step 2), so stepping back still shows what
   * was picked.
   */
  selectedIdsOverride?: string[];
  mode?: ModalTableMultiselectMode;
  scopeName?: TaskScopeNameEnum;
  /**
   * Full task scope, taking precedence over `scopeName` — for a caller that
   * needs a custom (argument-taking) scope, e.g. the Intervention form's
   * `{ custom: { eligibleForIntervention: { siteId, … } } }`, which returns
   * one row per recurring task with a check an intervention may still cover.
   * Pass a memoized value.
   */
  scope?: TaskScopeInputObject;
  /**
   * Renders single-select radio rows instead of checkboxes and swaps the
   * selection on click instead of appending to it. Pair with
   * `selectionLimit={1}` (e.g. Intervention's "Related task" field).
   */
  radioSelect?: boolean;
  selectionLimit?: number;
  /**
   * Filter field name -> forced value, (re-)applied every time the modal
   * opens — e.g. pin the Sites filter to the Site already chosen on the host
   * form. Pass a value memoized on its own content (not a fresh object
   * literal per render) — see `useModalTableModeManager`'s `filterStateDefault`
   * comment on why a stable reference matters here.
   */
  filterOverrides?: Values;
  /** Filter field names rendered non-interactive, typically ones present in `filterOverrides`. */
  disabledFilterFields?: string[];
  /** Filter field names not rendered at all. */
  hiddenFilterFields?: string[];
  /**
   * Extra filters ALWAYS applied to both the All and Selected tab queries,
   * on top of whatever the user's own filter row produces — for a caller
   * that needs to narrow this picker by a fixed condition rather than a
   * user-editable one.
   */
  baseFilters?: TaskFilterInputObject[];
};

// CURRENT scope returns one row per recurring chain (latest occurrence),
// deduping by group_key on the BE side so the modal doesn't show duplicate
// rows for recurring tasks. Callers whose mutation only accepts a narrower
// task scope (e.g. Departments' taskLinks, which the BE validates against
// Task.active) must pass `scopeName` to keep the picker from offering tasks
// the save will then reject as invalid.
const DEFAULT_SCOPE_NAME = TaskScopeNameEnum.Current;

function TasksTableMultiselect({
  filterLoadOptions,
  isOpen,
  onClose,
  onSelect,
  inline,
  ref,
  onSelectionConfirmed,
  formRef,
  fieldName = 'taskIds',
  selectedIdsOverride,
  mode = ModalTableMultiselectMode.WithTabs,
  scopeName = DEFAULT_SCOPE_NAME,
  scope: scopeOverride,
  radioSelect,
  selectionLimit,
  filterOverrides,
  disabledFilterFields,
  hiddenFilterFields,
  baseFilters,
}: TasksTableMultiselectProps) {
  const scope = useMemo(
    () => scopeOverride ?? { name: scopeName },
    [scopeOverride, scopeName],
  );
  const { t } = useTranslation();

  const filterStateDefault = useMemo(
    () => ({
      [FilterFieldNames.Sectors]: [],
      [FilterFieldNames.Sites]: [],
      [FilterFieldNames.SiteArea]: [],
      [FilterFieldNames.Equipment]: [],
      [FilterFieldNames.FormCategories]: [],
      ...filterOverrides,
    }),
    [filterOverrides],
  );

  const pickerState = useTaskPickerState<Task, TaskFilterInputObject>({
    isOpen,
    mode,
    formRef,
    fieldName,
    selectedIdsOverride,
    filterStateDefault,
    filterOverrides,
    selectionLimit,
    baseFilters,
    createAdditionalFilters,
  });
  const {
    activeTabState,
    additionalFilters,
    additionalFiltersSelected,
    clearFilter,
    filterState,
    filterStateSelected,
    idsForSelectedTabQuery,
    isFetchingAllIds,
    isSelectedOnlyMode,
    isSelectedTabActive,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    searchState,
    searchStateSelected,
    selectedIds: selectedTasks,
    setIsFetchingAllIds,
    setSelectedIdsForQuery,
    shouldRunAllQuery,
  } = pickerState;

  // All tab query
  const {
    collection,
    loading: tasksLoading,
    firstLoading: tasksFirstLoading,
    totalCount,
    fetchIds,
  } = useTasksMultiselectQueryHook({
    searchQuery: searchState?.[0] || '',
    paginationParams: {
      ...(orderingState?.[0] || {}),
      ...(paginationState?.[0] || {}),
    },
    orderingParams: orderingState?.[0] || {},
    additionalFilters,
    scope,
    skip: !shouldRunAllQuery,
    fetchPolicy: 'cache-first',
  });

  // Documentation→Task links are keyed by `group_key` on the BE, so the
  // modal must expose `groupKey` (not the per-instance `id`) as the row
  // identity the parent form receives via `onSelect`.
  const keyTaskByGroup = useCallback(
    (task: Task): Task => ({ ...task, id: task.groupKey ?? task.id }),
    [],
  );
  const collectionByGroup = useMemo(
    () => collection.map(keyTaskByGroup),
    [collection, keyTaskByGroup],
  );

  const {
    collection: allTabCollection,
    totalCount: allTabTotalCount,
    firstLoading: allTabFirstLoading,
  } = useAllTabSnapshot(pickerState, {
    collection: collectionByGroup,
    totalCount,
    loading: tasksLoading,
    firstLoading: tasksFirstLoading,
  });

  const fetchIdsByGroupKey = useCallback(async () => {
    const response = await fetchIds();
    const items = _get(response, 'data.data.collection', []) as Array<{
      id: string;
      groupKey?: string | null;
    }>;
    const seen = new Set<string>();
    const dedupedCollection: Array<{ id: string }> = [];
    items.forEach(({ id, groupKey }) => {
      const key = groupKey ?? id;
      if (seen.has(key)) return;
      seen.add(key);
      dedupedCollection.push({ id: key });
    });
    return {
      ...response,
      data: {
        ...(response?.data ?? {}),
        data: { collection: dedupedCollection },
      },
    } as Awaited<ReturnType<typeof fetchIds>>;
  }, [fetchIds]);

  // Selected tab query — filters by groupKey IN idsForSelectedTabQuery, which
  // holds the recurring-chain group keys stored in the form state. This works
  // in both create mode (no documentationId) and edit mode, mirroring the
  // id-based approach used by Assets/SiteAreas.
  const {
    collection: collectionSelectedRaw,
    loading: tasksLoadingSelected,
    firstLoading: tasksFirstLoadingSelected,
    totalCount: totalCountSelectedRaw,
  } = useTasksMultiselectQueryHook({
    searchQuery: searchStateSelected[0],
    paginationParams: {
      ...orderingStateSelected[0],
      ...paginationStateSelected[0],
    },
    orderingParams: orderingStateSelected[0],
    additionalFilters: isSelectedOnlyMode
      ? undefined
      : additionalFiltersSelected,
    groupKeys: idsForSelectedTabQuery,
    scope,
    skip:
      !isOpen || !isSelectedTabActive || idsForSelectedTabQuery.length === 0,
    fetchPolicy: 'cache-first',
  });

  const collectionSelected = useMemo(
    () =>
      idsForSelectedTabQuery.length === 0
        ? []
        : collectionSelectedRaw.map(keyTaskByGroup),
    [collectionSelectedRaw, idsForSelectedTabQuery.length, keyTaskByGroup],
  );
  const totalCountSelected =
    idsForSelectedTabQuery.length === 0 ? 0 : totalCountSelectedRaw;

  const columns = getTasksColumns({ t });

  // Base ModalTableMultiselect's onSelect only ever surfaces ids (already
  // group-keyed here via `keyTaskByGroup`). Cache every row seen across the
  // All/Selected tabs so callers that need the full entity (e.g. to build a
  // single-select field's chip label) can resolve it — mirrors
  // DepartmentParentSelectModal's known-items cache.
  const knownTasksRef = useRef<Map<string, Task>>(new Map());
  allTabCollection.forEach((item) => {
    knownTasksRef.current.set(String(item.id), item);
  });
  collectionSelected.forEach((item) => {
    knownTasksRef.current.set(String(item.id), item);
  });
  useEffect(() => {
    if (!isOpen) knownTasksRef.current.clear();
  }, [isOpen]);

  const filterFormFields = useFilterFields({
    ...filterLoadOptions,
    disabledFields: disabledFilterFields,
    hiddenFields: hiddenFilterFields,
  });

  // SelectedOnly mode
  if (isSelectedOnlyMode) {
    return (
      <ModalTableMultiunselect<Task>
        caption={t('details-summary')}
        columns={columns}
        dataSelected={collectionSelected}
        firstLoadingSelected={tasksFirstLoadingSelected}
        isOpen={isOpen}
        loadingSelected={tasksLoadingSelected || isFetchingAllIds}
        onClose={onClose}
        onSelect={onSelect}
        onSelectedIdsChange={setSelectedIdsForQuery}
        orderingStateSelected={orderingStateSelected}
        paginationStateSelected={paginationStateSelected}
        searchPlaceholder={t('tasks-multiselect-search-placeholder')}
        searchStateSelected={searchStateSelected}
        selected={selectedTasks}
        totalEntriesSelected={totalCountSelected}
      />
    );
  }

  // WithTabs mode
  if (
    !activeTabState ||
    !searchState ||
    !paginationState ||
    !orderingState ||
    !filterState
  ) {
    return null;
  }

  // Shared by both the standalone-modal and the inline (host-owned chrome)
  // renderings — only the wrapper around the picker body differs.
  const tableProps = {
    activeTabState,
    columns,
    data: allTabCollection,
    dataSelected: collectionSelected,
    fetchIds: fetchIdsByGroupKey,
    filterFormFields,
    filterState,
    filterStateSelected,
    filterStateDefault,
    firstLoading: allTabFirstLoading,
    firstLoadingSelected: tasksFirstLoadingSelected,
    isOpen,
    loading: shouldRunAllQuery ? tasksLoading : false,
    loadingSelected: tasksLoadingSelected || isFetchingAllIds,
    onIsFetchingAllIdsChange: setIsFetchingAllIds,
    onResetFilter: clearFilter,
    onSelect: (values: string[]) => {
      const selectedItems = values
        .map((taskId) => knownTasksRef.current.get(taskId))
        .filter((item): item is Task => !!item);
      onSelect(values, selectedItems);
    },
    onSelectedIdsChange: setSelectedIdsForQuery,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    radioSelect,
    searchPleceholder: t('tasks-multiselect-search-placeholder'),
    searchState,
    searchStateSelected,
    selected: selectedTasks,
    selectionLimit,
    totalEntries: allTabTotalCount,
    totalEntriesSelected: totalCountSelected,
  };

  return (
    <TaskPickerTable<Task>
      tableProps={tableProps}
      inline={inline}
      ref={ref}
      onSelectionConfirmed={onSelectionConfirmed}
      caption={t(selectionLimit === 1 ? 'task-select' : 'task-multiselect')}
      onClose={onClose}
    />
  );
}

export default memo(TasksTableMultiselect);
